import React, { useEffect, useState } from "react";
import { useLocation, Link, Navigate } from "react-router-dom";
import { services } from "@/data/services";
import { locationList } from "@/data/locations";
import { useLang } from "@/lib/i18n";
import { useSEO, breadcrumbJsonLd, serviceJsonLd, faqPageJsonLd, siteUrl } from "@/lib/seo";
import { CheckCircle2, ArrowRight, ArrowLeft } from "lucide-react";
import { fetchFAQs } from "@/lib/api";
import ContactForm from "@/components/ContactForm";
import FAQAccordion from "@/components/FAQAccordion";
import CTASection from "@/components/CTASection";

// Inline markdown-style links [text](path) → React Router <Link>.
// Used so we can put natural in-text city links inside services.js section bodies
// without needing a markdown renderer for short blocks.
const renderInlineLinks = (text) => {
  if (typeof text !== "string" || !text.includes("[")) return text;
  const parts = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0; let m; let i = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    const [, anchor, href] = m;
    parts.push(
      <Link key={`il-${i++}`} to={href} className="text-[#00D4FF] hover:underline">{anchor}</Link>
    );
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
};

// Map each service slug to the FAQ category that's most relevant for that page
const SLUG_TO_FAQ_CATEGORY = {
  "mobiele-truckwash": "truckwash",
  "vrachtwagenreiniging": "truckwash",
  "fleet-cleaning": "fleet",
  "wagenpark-reiniging": "fleet",
  "haccp-certificaten": "haccp",
};

const ServicePage = () => {
  const location = useLocation();
  const slug = location.pathname.replace(/^\//, "").split("/")[0];
  const service = services[slug];
  const { lang, t } = useLang();
  const [faqs, setFaqs] = useState([]);

  const faqCategory = SLUG_TO_FAQ_CATEGORY[slug];
  useEffect(() => {
    if (faqCategory) {
      fetchFAQs(faqCategory).then(setFaqs).catch(() => {});
    }
  }, [faqCategory]);

  const content = (service && service[lang]) || (service && service.nl) || { title: "", meta_title: "", meta_desc: "", h1: "", lead: "", sections: [], bullets: [] };
  const canonical = service ? siteUrl(`/${service.slug}`) : siteUrl("/");

  useSEO({
    title: content.meta_title,
    description: content.meta_desc,
    canonical,
    image: service ? service.image : undefined,
    lang,
    jsonLd: service
      ? [
          {
            id: "service-jsonld",
            data: serviceJsonLd({
              name: content.title,
              description: content.meta_desc,
              url: canonical,
            }),
          },
          {
            id: "breadcrumb-jsonld",
            data: breadcrumbJsonLd([
              { name: "Home", url: siteUrl("/") },
              { name: content.title, url: canonical },
            ]),
          },
          ...(faqs.length > 0
            ? [{ id: "service-faq-jsonld", data: faqPageJsonLd(faqs, lang) }]
            : []),
        ]
      : [],
  });

  if (!service) return <Navigate to="/" replace />;

  return (
    <main data-testid={`service-page-${service.slug}`} className="min-h-screen pb-0">
      {/* Hero */}
      <section className="relative pt-12 pb-16 sm:pt-20 sm:pb-24 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src={service.image}
            alt={content.h1}
            className="w-full h-full object-cover opacity-25"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050509]/70 via-[#050509]/85 to-[#050509]" />
        </div>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <Link to="/" className="text-xs text-white/50 hover:text-[#00D4FF] inline-flex items-center gap-1.5 mb-6">
            <ArrowLeft className="w-3 h-3" /> {t("common.back")}
          </Link>
          <p className="section-overline mb-3">{lang === "nl" ? "Dienst" : "Service"}</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white max-w-4xl leading-[1.05]">
            {content.h1}
          </h1>
          <p className="mt-6 text-lg text-white/65 max-w-2xl leading-relaxed">{content.lead}</p>
        </div>
      </section>

      {/* Content + bullets */}
      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7 space-y-12">
            {content.sections.map((s, i) => (
              <article key={i}>
                <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-4">{s.title}</h2>
                <p className="text-white/65 text-base sm:text-[17px] leading-relaxed">{renderInlineLinks(s.body)}</p>
                {s.levels && s.levels.length > 0 && (
                  <div className="mt-6 space-y-3" data-testid={`service-${service.slug}-levels`}>
                    {s.levels.map((lvl, j) => (
                      <div
                        key={j}
                        className="group relative rounded-xl border border-white/10 bg-[#0F0F23] p-5 sm:p-6 transition-colors hover:border-[#00D4FF]/40"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-5">
                          <div className="flex items-center gap-3 mb-2 sm:mb-0 sm:min-w-[110px]">
                            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/30 text-[#00D4FF] mono text-sm font-bold">
                              {String(j + 1).padStart(2, "0")}
                            </span>
                            <span className="text-[11px] uppercase tracking-[0.2em] text-[#00D4FF] font-semibold">
                              {lvl.tier}
                            </span>
                          </div>
                          <div className="flex-1">
                            <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight mb-1.5">
                              {lvl.name}
                            </h3>
                            <p className="text-white/65 text-[15px] leading-relaxed">{lvl.body}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
          <aside className="lg:col-span-5">
            <div className="sticky top-24 rounded-2xl border border-white/10 bg-[#0F0F23] p-8">
              <p className="section-overline mb-3">{lang === "nl" ? "Inbegrepen" : "Included"}</p>
              <h3 className="text-2xl font-bold text-white mb-6">
                {lang === "nl" ? "Wat u krijgt" : "What you get"}
              </h3>
              <ul className="space-y-3.5">
                {content.bullets.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/75 text-[15px]">
                    <CheckCircle2 className="w-5 h-5 text-[#00D4FF] mt-0.5 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <Link to="/contact?type=quote" data-testid={`service-${service.slug}-cta`}>
                <button className="btn-primary w-full justify-center mt-8">
                  {t("common.request_quote")} <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* Inline contact */}
      <section className="py-16 sm:py-24 bg-[#0a0a18] border-y border-white/5">
        <div className="mx-auto max-w-3xl px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="section-overline mb-3">{lang === "nl" ? "Direct contact" : "Direct contact"}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              {lang === "nl" ? "Vraag een offerte voor deze dienst" : "Request a quote for this service"}
            </h2>
          </div>
          <ContactForm
            defaultType="quote"
            dataTestid={`service-${service.slug}-form`}
            hideTypeSelector
          />
        </div>
      </section>

      {faqs.length > 0 && (
        <section className="py-20 sm:py-24">
          <div className="mx-auto max-w-3xl px-6 lg:px-8">
            <div className="text-center mb-10">
              <p className="section-overline mb-3">FAQ</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                {lang === "nl" ? "Veelgestelde vragen" : "Frequently asked questions"}
              </h2>
            </div>
            <FAQAccordion items={faqs} dataTestid={`service-${service.slug}-faq`} />
          </div>
        </section>
      )}

      {/* RELATED SERVICES — internal-linking authority distribution */}
      <section
        data-testid={`service-${service.slug}-related`}
        className="py-20 sm:py-24 bg-[#0a0a18] border-t border-white/5"
      >
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
            <div>
              <p className="section-overline mb-3">
                {lang === "nl" ? "Gerelateerd" : "Related"}
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
                {lang === "nl" ? "Gerelateerde diensten" : "Related services"}
              </h2>
            </div>
            <p className="text-white/55 text-sm max-w-md">
              {lang === "nl"
                ? "Bekijk onze andere truckwash- en wagenparkdiensten — alle gekoppeld aan WaszBase."
                : "Browse our other truckwash and fleet services — all integrated with WaszBase."}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.values(services)
              .filter((s) => s.slug !== service.slug)
              .map((s) => {
                const sc = (s[lang] || s.nl);
                return (
                  <Link
                    key={s.slug}
                    to={`/${s.slug}`}
                    data-testid={`related-service-${s.slug}`}
                    className="group relative overflow-hidden rounded-xl border border-white/10 bg-[#0F0F23] p-5 card-hover h-full flex flex-col"
                  >
                    <h3 className="text-white text-base font-semibold tracking-tight mb-2 group-hover:text-[#00D4FF] transition-colors">
                      {sc.title}
                    </h3>
                    <p className="text-white/55 text-[13px] leading-relaxed flex-1">
                      {sc.meta_desc.length > 110 ? sc.meta_desc.slice(0, 107) + "…" : sc.meta_desc}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1 text-[12px] font-semibold text-[#00D4FF]">
                      {lang === "nl" ? "Lees meer" : "Read more"}
                      <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </Link>
                );
              })}
          </div>

          {/* Cross-link strip: WaszBase + Locations */}
          <div className="mt-10 pt-8 border-t border-white/5 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-white text-sm font-semibold mb-3 uppercase tracking-[0.16em]">
                {lang === "nl" ? "Digitaal dossier" : "Digital dossier"}
              </h3>
              <p className="text-white/55 text-sm leading-relaxed">
                {lang === "nl" ? (
                  <>
                    Elke wasbeurt komt automatisch terecht in{" "}
                    <Link to="/waszbase" data-testid={`related-${service.slug}-waszbase-link`} className="text-[#00D4FF] hover:underline underline-offset-4 font-semibold">
                      WaszBase
                    </Link>
                    : kentekenregistratie, HACCP-certificaten en NVWA-export. Zie ook onze{" "}
                    <Link to="/haccp-certificaten" className="text-[#00D4FF] hover:underline underline-offset-4 font-semibold">
                      HACCP-werkwijze
                    </Link>
                    .
                  </>
                ) : (
                  <>
                    Every wash is automatically logged in{" "}
                    <Link to="/waszbase" data-testid={`related-${service.slug}-waszbase-link`} className="text-[#00D4FF] hover:underline underline-offset-4 font-semibold">
                      WaszBase
                    </Link>
                    : license-plate registry, HACCP certificates and NVWA export. See also our{" "}
                    <Link to="/haccp-certificaten" className="text-[#00D4FF] hover:underline underline-offset-4 font-semibold">
                      HACCP workflow
                    </Link>
                    .
                  </>
                )}
              </p>
            </div>
            <div>
              <h3 className="text-white text-sm font-semibold mb-3 uppercase tracking-[0.16em]">
                {lang === "nl" ? "Lokaal actief" : "Locally active"}
              </h3>
              <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
                {locationList.map((loc) => (
                  <Link
                    key={loc.slug}
                    to={`/truckwash-${loc.slug}`}
                    data-testid={`related-${service.slug}-loc-${loc.slug}`}
                    className="text-white/65 hover:text-[#00D4FF] underline-offset-4 hover:underline transition-colors"
                  >
                    Truckwash {loc.city}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </main>
  );
};

export default ServicePage;
